import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import { getCookie } from "../../utils/cookies";

function getAppSession() {
  const userData = JSON.parse(getCookie("userData") || "{}");
  return {
    appid: "ROUNDPAYAPPID13APR20191351",
    imei: "d593bdd26dfbea56",
    loginTypeID: 1,
    regKey: "",
    serialNo: "d593bdd26dfbea56",
    version: "1.0",
    uid: 0,
    session: getCookie("session") || "",
    sessionID: getCookie("sessionID") || "",
    userID: userData.userID || "",
  };
}

function getVideoDuration(url) {
  return new Promise((resolve) => {
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.src = url;
    probe.onloadedmetadata = () => resolve(probe.duration || 0);
    probe.onerror = () => resolve(0);
  });
}

export default function MyCourses() {
  const navigate = useNavigate();
  const [videoList, setVideoList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [watchProgress, setWatchProgress] = useState({});
  const [isAccountActive, setIsAccountActive] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(true);
  const videoRef = useRef(null);
  const watchIntervalRef = useRef(null);
  const resumeFromRef = useRef(0);

  useEffect(() => {
    fetchVideoList();
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");
      const res = await apiClient.post("/Dashboard/MataMaskDashboard", {
        appid: "ROUNDPAYAPPID13APR20191351",
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        setIsAccountActive(res.data?.isTopup === true);
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  useEffect(() => {
    return () => stopWatchTracking();
  }, []);

  const fetchVideoList = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/GetVideo", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
        }, 
        Request: {
          DisplayType: 1,
        },
      });

      if (res.statuscode == 1) {
        const videos = res.videoviewModel?.course || [];
        setVideoList(videos);
        loadWatchProgress(videos);
        updateVideoDurations(videos);
      } else {
        setVideoList([]);
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateVideoDurations = async (videos) => {
    try {
      const videoArray = await Promise.all(
        videos.map(async (video) => ({
          VideoId: video.videoId,
          VideoDuration: Math.floor(await getVideoDuration(video.url)),
        })),
      );

      await apiClient.post("/App/UpdateVideoDuration", {
        appSession: getAppSession(),
        Request: {
          Video: videoArray,
        },
      });
    } catch (err) {
      console.log("error", err);
    }
  };

  const fetchWatchTime = async (videoId) => {
    try {
      const res = await apiClient.post("/App/GetAddVideoWatchTimeById", {
        appSession: getAppSession(),
        request: {
          VideoId: String(videoId),
        },
      });

      if (res.statuscode == 1) {
        return {
          resumeFrom: Number(res.resumeFrom) || 0,
          isCompleted: !!res.isCompleted,
        };
      }
    } catch (err) {
      console.log("error", err);
    }
    return { resumeFrom: 0, isCompleted: false };
  };

  async function loadWatchProgress(videos) {
    try {
      const res = await apiClient.post("/App/GetAddVideoWatchTimeById", {
        appSession: getAppSession(),
        request: {
          VideoId: 0,
        },
      });

      if (res.statuscode !== 1) return;

      const progressByVideoId = {};
      (res.data || []).forEach((item) => {
        progressByVideoId[item.videoId] = {
          resumeFrom: Number(item.resumeFrom) || 0,
          isCompleted: !!item.isCompleted,
        };
      });

      const entries = await Promise.all(
        videos.map(async (video) => {
          const info = progressByVideoId[video.videoId];
          if (!info) return [video.videoId, 0];
          if (info.isCompleted) return [video.videoId, 100];
          if (info.resumeFrom <= 0) return [video.videoId, 0];

          const duration = await getVideoDuration(video.url);
          const percent =
            duration > 0
              ? Math.min(100, (info.resumeFrom / duration) * 100)
              : 0;
          return [video.videoId, percent];
        }),
      );
      setWatchProgress(Object.fromEntries(entries));
    } catch (err) {
      console.log("error", err);
    }
  }

  const saveWatchTime = (videoId, watchTime, videoDuration) => {
    apiClient
      .post("/App/AddVideoWatchTime", {
        appSession: getAppSession(),
        request: {
          VideoId: String(videoId),
          WatchTime: String(Math.floor(watchTime) || 0),
          VideoDuration: String(Math.floor(videoDuration) || 0),
        },
      })
      .catch((err) => console.log("error", err));
  };

  function stopWatchTracking() {
    if (watchIntervalRef.current) {
      clearInterval(watchIntervalRef.current);
      watchIntervalRef.current = null;
    }
  }

  function reportCurrentProgress() {
    if (videoRef.current && activeVideo) {
      saveWatchTime(
        activeVideo.videoId,
        videoRef.current.currentTime,
        videoRef.current.duration,
      );
    }
  }

  function startWatchTracking() {
    stopWatchTracking();
    watchIntervalRef.current = setInterval(reportCurrentProgress, 10000);
  }

  async function handleOpenVideo(video) {
    resumeFromRef.current = 0;
    setActiveVideo(video);
    const { resumeFrom: savedResumeFrom } = await fetchWatchTime(
      video.videoId,
    );
    resumeFromRef.current = savedResumeFrom;

    if (
      videoRef.current &&
      videoRef.current.readyState >= 1 &&
      savedResumeFrom > 0 &&
      savedResumeFrom < videoRef.current.duration
    ) {
      videoRef.current.currentTime = savedResumeFrom;
    }
  }

  function handleCloseVideo() {
    reportCurrentProgress();
    stopWatchTracking();

    if (videoRef.current && activeVideo && videoRef.current.duration > 0) {
      const percent = Math.min(
        100,
        (videoRef.current.currentTime / videoRef.current.duration) * 100,
      );
      setWatchProgress((prev) => ({ ...prev, [activeVideo.videoId]: percent }));
    }

    setActiveVideo(null);
  }

  return (
    <>
      <div className="content" style={{ position: "relative" }}>
        <section className="page active" id="page-courses">
          <div
            id="coursesLocked"
            className="content-lock"
            style={{ display: isAccountActive ? "none" : "flex" }}
          >
            <div className="icon-circle">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <rect x={4} y={10} width={16} height={10} rx={2} />
                <path d="M8 10V7a4 4 0 018 0v3" />
              </svg>
            </div>
            <h3>Courses are locked</h3>
            <p>
              Activate your package to access all recorded video lessons and PDF
              study material.
            </p>
            <button
              className="btn-primary"
              style={{ width: "auto", padding: "11px 22px" }}
              onClick={() => navigate("/panel/activate_package")}
            >
              Activate Now
            </button>
          </div>
          <div
            id="coursesUnlocked"
            style={{ display: isAccountActive ? "block" : "none" }}
          >
            <h3 className="section-title">My Courses</h3>

            <div
              className="card"
              style={{
                marginBottom: 20,
                borderColor: "rgba(230,181,89,0.25)",
                background:
                  "linear-gradient(135deg, rgba(230,181,89,0.06), var(--bg-elev))",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--gold-bright)"
                  strokeWidth={2}
                  width={18}
                  height={18}
                  style={{ flexShrink: 0 }}
                >
                  <circle cx={12} cy={12} r={10} />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <h4
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--gold-bright)",
                  }}
                >
                  Important Notice
                </h4>
              </div>
              <p
                style={{
                  fontSize: 12.5,
                  color: "var(--text-dim)",
                  lineHeight: 1.7,
                  margin: "0 0 12px",
                }}
              >
                <b style={{ color: "var(--text)" }}>Copyright & Usage:</b>{" "}
                All L2E Academy course content is for personal learning only.
                Copying, sharing, recording, reselling, or distributing the
                content without permission is strictly prohibited and may
                lead to legal action or access suspension.
              </p>
              <p
                style={{
                  fontSize: 12.5,
                  color: "var(--text-dim)",
                  lineHeight: 1.7,
                  margin: 0,
                }}
              >
                <b style={{ color: "var(--text)" }}>Risk Disclaimer:</b>{" "}
                Stock market trading involves market risk. This program is
                for educational purposes only and does not guarantee profits
                or returns.
              </p>
            </div>

            <div className="grid grid-3">
              {isLoading ? (
                <p style={{ color: "var(--text-faint)" }}>
                  Loading courses...
                </p>
              ) : videoList.length === 0 ? (
                <p style={{ color: "var(--text-faint)" }}>
                  No courses available.
                </p>
              ) : (
                videoList.map((video) => (
                  <div className="course-card" key={video.videoId}>
                    <div
                      className="course-thumb"
                      style={{
                        background: video.thumbnail
                          ? `url(${video.thumbnail}) center/cover no-repeat`
                          : "linear-gradient(135deg,#241a10,#100e0a)",
                      }}
                    >
                      <div
                        className="thumb-play"
                        onClick={() => handleOpenVideo(video)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      {watchProgress[video.videoId] > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: "rgba(255,255,255,0.25)",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${watchProgress[video.videoId]}%`,
                              background: "#e5342b",
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="course-body">
                      <h4>{video.title}</h4>
                      <div style={{ margin: "8px 0" }}>
                        <span
                          className={`badge ${video.status ? "green" : "red"}`}
                        >
                          {video.status ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <button
                        className="course-btn"
                        onClick={() => handleOpenVideo(video)}
                      >
                        Watch Now
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {isDisclaimerOpen && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(6,8,12,0.78)",
              backdropFilter: "blur(4px)",
              zIndex: 2000,
              padding: 16,
            }}
            onClick={() => setIsDisclaimerOpen(false)}
          >
            <div
              style={{
                position: "sticky",
                top: "3vh",
                width: "100%",
                maxWidth: 540,
                maxHeight: "94vh",
                margin: "3vh auto",
                overflowY: "auto",
                background:
                  "linear-gradient(160deg, var(--bg-elev-2), var(--bg-elev))",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                boxShadow: "0 40px 100px -30px rgba(0,0,0,0.7)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span
                onClick={() => setIsDisclaimerOpen(false)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 14,
                  color: "var(--text-faint)",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  zIndex: 1,
                }}
              >
                &times;
              </span>

              <div
                style={{
                  padding: "14px 22px 10px",
                  borderBottom: "1px solid var(--border-soft)",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/assets/panel/images/white-logo.png"
                  alt="L2E Academy"
                  style={{ height: 30, width: "auto" }}
                />
              </div>

              <div style={{ padding: "14px 22px 18px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background:
                        "linear-gradient(135deg, var(--gold), var(--gold-bright))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1a1408"
                      strokeWidth={2}
                      width={15}
                      height={15}
                    >
                      <path d="M12 2l8 3v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V5l8-3z" />
                      <path d="M9 12h6M9 15h4" />
                    </svg>
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 15.5,
                      fontWeight: 800,
                      color: "var(--gold-bright)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    Copyright & Usage Disclaimer
                  </h3>
                </div>

                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-dim)",
                    lineHeight: 1.5,
                    margin: "0 0 8px",
                  }}
                >
                  This course and all its contents — including videos,
                  strategies, notes, and downloadable materials — are the
                  intellectual property of{" "}
                  <b style={{ color: "var(--text)" }}>L2E Academy</b>. They
                  are provided{" "}
                  <b style={{ color: "var(--gold-bright)" }}>
                    solely for your personal learning
                  </b>
                  .
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-dim)",
                    lineHeight: 1.5,
                    margin: "0 0 8px",
                  }}
                >
                  Copying, reproducing, sharing, reselling, or distributing
                  any part of this content in any form — including
                  screenshots, recordings, or file transfers — is{" "}
                  <b style={{ color: "var(--gold-bright)" }}>
                    strictly prohibited
                  </b>{" "}
                  under copyright law. Unauthorized sharing may result in{" "}
                  <b style={{ color: "var(--gold-bright)" }}>legal action</b>{" "}
                  and{" "}
                  <b style={{ color: "var(--gold-bright)" }}>
                    permanent suspension of access
                  </b>
                  .
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-dim)",
                    lineHeight: 1.5,
                    margin: "0 0 12px",
                  }}
                >
                  Please respect the effort and time invested in creating
                  this program — your honesty helps us keep bringing more
                  valuable learning to traders like you.
                </p>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "var(--bg-elev-3)",
                    border: "1px solid rgba(230,181,89,0.25)",
                    borderRadius: 10,
                    padding: "9px 12px",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--gold-bright)"
                    strokeWidth={2}
                    width={17}
                    height={17}
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M3 17l6-6 4 4 8-8" />
                    <path d="M15 7h6v6" />
                  </svg>
                  <p
                    style={{
                      fontSize: 11.5,
                      color: "var(--text-dim)",
                      lineHeight: 1.45,
                      margin: 0,
                    }}
                  >
                    <b style={{ color: "var(--text)" }}>
                      Stock market trading involves market risk.
                    </b>{" "}
                    This program is for educational purposes and does not
                    guarantee profits or returns.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn-primary"
                  style={{ width: "100%", marginTop: 14 }}
                  onClick={() => setIsDisclaimerOpen(false)}
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {activeVideo && (
        <div className="modal-overlay active">
          <div className="modal wide">
            <span className="modal-close" onClick={handleCloseVideo}>
              &times;
            </span>
            <div className="modal-head">
              <h3>{activeVideo.title}</h3>
            </div>
            <div style={{ position: "relative", paddingTop: "56.25%" }}>
              <video
                key={activeVideo.videoId}
                ref={videoRef}
                src={activeVideo.url}
                controls
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                autoPlay
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                  background: "#000",
                }}
                onLoadedMetadata={() => {
                  if (
                    videoRef.current &&
                    resumeFromRef.current > 0 &&
                    resumeFromRef.current < videoRef.current.duration
                  ) {
                    videoRef.current.currentTime = resumeFromRef.current;
                  }
                }}
                onPlay={startWatchTracking}
                onPause={() => {
                  stopWatchTracking();
                  reportCurrentProgress();
                }}
                onEnded={() => {
                  stopWatchTracking();
                  reportCurrentProgress();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}





 